// Copyright (c) 2026 Solartek LLC. All rights reserved.

import type { ApiErrorBody } from '@flowza/contracts';
import { supabase } from './supabase.js';
import { env } from './env.js';
import i18n from './i18n.js';

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string, readonly requestId?: string, readonly details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Status used for failures that never produced an HTTP response at all — DNS, TLS, connection refused, a timeout, or
 * a CORS rejection. `fetch` reports every one of these as an opaque `TypeError`, so without this they would surface as
 * a non-`ApiError` and skip every `instanceof ApiError` branch in the UI (including the MFA gate).
 */
export const NETWORK_ERROR_STATUS = 0;

/**
 * True for the 403 the API returns when the session must step up to `aal2` (apps/api `middleware/mfa.ts`):
 * either the organisation requires MFA, or the caller is a platform admin, who is gated on every route.
 */
export function isMfaRequiredError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403 && error.details?.reason === 'MFA_REQUIRED';
}

/**
 * True for the API's 403 FORBIDDEN: the caller's role does not carry the permission, or the record is outside their branch
 * scope. That is an answer about access, not a failure — a retry gets the same answer and support has nothing to look up —
 * so screens show "permission denied" instead of an error with a request id. A 403 that carries a `details.reason` is a
 * policy answer with its own screen, not the role's: the MFA step-up, a punch outside the geofence, regularisation or selfie
 * check-in switched off (apps/api services/portal).
 */
export function isPermissionDeniedError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403 && error.code === 'FORBIDDEN' && typeof error.details?.reason !== 'string';
}

/**
 * True for the API's 404 NOT_FOUND of a record: it does not exist, or row-level security hides it because it is outside the
 * caller's scope (another branch, not a direct report). Never the router's 404 (FEATURE_UNAVAILABLE).
 */
export function isRecordNotFoundError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404 && error.code === 'NOT_FOUND';
}

/** The module key of the API's 403 for a module the organisation does not have (FEATURE_DISABLED, `details.reason` MODULE_DISABLED). */
export function disabledModuleOf(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 403 || error.details?.reason !== 'MODULE_DISABLED') return null;
  return typeof error.details.module === 'string' ? error.details.module : null;
}

/** True when the API could not be reached at all, as opposed to answering with an error. */
export function isNetworkError(error: unknown): boolean {
  return error instanceof ApiError && error.status === NETWORK_ERROR_STATUS;
}

/**
 * Why a request got no readable answer (`details.reason` of a network error). `fetch` rejects with the same opaque
 * TypeError in every case, and all of them used to read "check your connection" — so a security rule in front of the
 * API, which no connection check will ever clear, was reported as the user's own network and retried as if it were a
 * dropped packet.
 * - OFFLINE: the browser says it has no network.
 * - UNREACHABLE: not even the API host's health check answers — DNS, TLS, a firewall, a VPN or an extension.
 * - BLOCKED: the host answers, but not with the API: a CDN challenge, block or rate limit, or a gateway error while the
 *   API restarts. Those responses carry no CORS headers, so the browser hides them from the app.
 */
export type NetworkFailureReason = 'OFFLINE' | 'UNREACHABLE' | 'BLOCKED';

/** The network failure's reason, when the error is one (see NetworkFailureReason). */
export function networkFailureReason(error: unknown): NetworkFailureReason | undefined {
  if (!isNetworkError(error)) return undefined;
  const reason = (error as ApiError).details?.reason;
  return reason === 'OFFLINE' || reason === 'UNREACHABLE' || reason === 'BLOCKED' ? reason : undefined;
}

const PROBE_TIMEOUT_MS = 5_000;
let probeInFlight: Promise<boolean> | null = null;

/**
 * Whether the API host answers at all. A `no-cors` fetch resolves with an opaque response for ANY HTTP answer — a CDN's
 * 403 challenge or a gateway's 502 included — and rejects only when nothing answered. The API serves /api/health with
 * `Cross-Origin-Resource-Policy: cross-origin` for this probe (apps/api `middleware/security-headers.ts`). Requests
 * that fail together share one probe.
 */
function apiHostAnswers(): Promise<boolean> {
  probeInFlight ??= fetch(`${env.apiUrl}/api/health`, {
    mode: 'no-cors',
    cache: 'no-store',
    credentials: 'omit',
    signal: typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(PROBE_TIMEOUT_MS) : undefined,
  }).then(() => true, () => false).finally(() => { probeInFlight = null; });
  return probeInFlight;
}

async function diagnoseNetworkFailure(): Promise<NetworkFailureReason> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'OFFLINE';
  return (await apiHostAnswers()) ? 'BLOCKED' : 'UNREACHABLE';
}

const NETWORK_FAILURE_MESSAGE_KEYS = { OFFLINE: 'common.network.offline', UNREACHABLE: 'common.network.unreachable', BLOCKED: 'common.network.blocked' } as const;

/**
 * Retry policy for queries: a 4xx is an answer and will not change on a retry, anything else might. A network failure
 * is an `ApiError` too, with status 0 — and 0 < 500, so a bare "retry unless status < 500" test reads it as a client
 * error and never retries it. One dropped request on a flaky connection then became the full-screen "Could not reach
 * the API", because /me failed on its first and only attempt.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  return isNetworkError(error) || !(error instanceof ApiError && error.status < 500);
}

/**
 * Code given to a 404 from the API's router — the API does not serve that path at all. The web is deployed on every merge
 * and the API by hand, so for a while after a release a new screen can call an endpoint the running API does not have
 * yet. That is "not available yet", not an error to report: its message is translated here, once, so no screen, toast
 * or form ever shows the router's "Route not found.".
 */
export const FEATURE_UNAVAILABLE = 'FEATURE_UNAVAILABLE';

/** True for the 404 of a path the API does not serve (see FEATURE_UNAVAILABLE), never for a record that does not exist. */
export function isFeatureUnavailableError(error: unknown): boolean {
  return error instanceof ApiError && error.code === FEATURE_UNAVAILABLE;
}

/** The router's 404: tagged with `details.reason` since the API marks it; older APIs only say "Route not found.". */
function isRouteNotFound(status: number, body: Partial<ApiErrorBody>): boolean {
  if (status !== 404) return false;
  return body.details?.reason === 'ROUTE_NOT_FOUND' || body.message === 'Route not found.';
}

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  idempotencyKey?: string;
}

async function accessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export async function apiFetch<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
  const url = new URL(`${env.apiUrl}/api/v1${path}`);
  if (opts.query) for (const [k, v] of Object.entries(opts.query)) if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  const token = await accessToken();
  const headers = new Headers(opts.headers);
  headers.set('Accept', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (opts.body !== undefined && !(opts.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  if (opts.idempotencyKey) headers.set('Idempotency-Key', opts.idempotencyKey);
  let res: Response;
  try {
    res = await fetch(url, { ...opts, headers, body: opts.body instanceof FormData ? opts.body : opts.body !== undefined ? JSON.stringify(opts.body) : undefined });
  } catch (cause) {
    // No readable response. Carry the API origin in the message: there is no request id to quote, and "which host" plus
    // "unreachable or answered by something else" is what a user or a support ticket can act on.
    if (opts.signal?.aborted) throw cause; // the caller cancelled it: not a network failure, nothing to diagnose
    const reason = await diagnoseNetworkFailure();
    throw new ApiError(NETWORK_ERROR_STATUS, 'NETWORK_ERROR', i18n.t(NETWORK_FAILURE_MESSAGE_KEYS[reason], { url: env.apiUrl }), undefined, {
      reason,
      cause: cause instanceof Error ? cause.message : String(cause),
    });
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  // A proxy between the browser and the API (Cloudflare 5xx, a captive portal) answers with HTML, so parsing must not
  // be allowed to throw a SyntaxError over the real status — the status is the useful part.
  let json: unknown = null;
  try {
    json = text ? (JSON.parse(text) as unknown) : null;
  } catch {
    if (res.ok) throw new ApiError(res.status, 'INVALID_RESPONSE', 'The API returned a malformed response', undefined, { bodyPreview: text.slice(0, 200) });
  }
  if (!res.ok) {
    const err = (json ?? {}) as Partial<ApiErrorBody>;
    if (isRouteNotFound(res.status, err)) throw new ApiError(res.status, FEATURE_UNAVAILABLE, i18n.t('common.featureUnavailable'), err.requestId, { ...err.details, path });
    throw new ApiError(res.status, err.code ?? 'HTTP_ERROR', err.message ?? res.statusText, err.requestId, err.details);
  }
  return json as T;
}

export const api = {
  get: <T>(path: string, query?: ApiRequestOptions['query']) => apiFetch<T>(path, { method: 'GET', query }),
  post: <T>(path: string, body?: unknown, opts?: ApiRequestOptions) => apiFetch<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};

export interface Envelope<T> { data: T; meta?: Record<string, unknown> }
export interface PageEnvelope<T> { data: T[]; meta: { page: number; pageSize: number; total: number; totalPages: number } }
